import { useState } from 'react';
import axios from 'axios';

import type { PromotionPreviewResult, RefinePreviewParams } from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

import { hasNewMembers, type NewMemberKind } from './newMembers';

export interface NewMemberStep {
  /** New LOB / sub-org members currently opted in for promotion (default all — promote-all). */
  promotedLobIds: Set<string>;
  promotedSubOrgIds: Set<string>;
  /** True while the opt-out re-projection (refine) is in flight — disables Continue and blocks close. */
  isRefining: boolean;
  /** After a preview with new members, show the interim opt-in step before the change selector. */
  showMemberStep: boolean;
  /** Once the step is confirmed (or the preview has no new members), show the change selector. */
  showChangeSelector: boolean;
  /** Seed the step to "promote all" from a fresh preview's new members. */
  seedPromotedMembers: (result: PromotionPreviewResult) => void;
  handleToggleMember: (kind: NewMemberKind, externalId: string, checked: boolean) => void;
  /** Advance past the interim step, refining over the pinned exports when any member was opted out. */
  handleConfirmMembers: () => Promise<void>;
  /** Reset the step's own state (opted-in ids + confirmation) — on close / re-preview / submit error. */
  resetMembers: () => void;
}

/** Shared interim "New members" step for both promotion create flows (live cross-org + uploaded artifact).
 * Both dialogs surface the same opt-in step, seed it to promote-all from the preview, and confirm it via the
 * same read-only `/preview/refine` re-projection — so that state and the promote-all-skips-refine logic live
 * here rather than being re-derived per dialog (mirrors {@link useConflictResolution}). The dialog still owns
 * `preview` (shared with the change selector / submit); `resetSelection` clears the dialog-owned change
 * selection + submit-blocked flag on a refine failure, where the step also resets its own state. */
export function useNewMemberStep({
  preview,
  setPreview,
  baseRoute,
  resetSelection,
}: {
  preview: PromotionPreviewResult | null;
  setPreview: (preview: PromotionPreviewResult | null) => void;
  baseRoute: string;
  resetSelection: () => void;
}): NewMemberStep {
  const [promotedLobIds, setPromotedLobIds] = useState<Set<string>>(new Set());
  const [promotedSubOrgIds, setPromotedSubOrgIds] = useState<Set<string>>(new Set());
  const [membersConfirmed, setMembersConfirmed] = useState(false);
  const [isRefining, setIsRefining] = useState(false);

  // Seed the interim step to "promote all" from a fresh preview's new members (the promote-all default).
  const seedPromotedMembers = (result: PromotionPreviewResult) => {
    setPromotedLobIds(new Set((result.new_members?.lobs ?? []).map((member) => member.external_id)));
    setPromotedSubOrgIds(new Set((result.new_members?.sub_organizations ?? []).map((member) => member.external_id)));
    setMembersConfirmed(false);
  };

  const handleToggleMember = (kind: NewMemberKind, externalId: string, checked: boolean) => {
    const setter = kind === 'lob' ? setPromotedLobIds : setPromotedSubOrgIds;
    setter((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(externalId);
      } else {
        next.delete(externalId);
      }
      return next;
    });
  };

  const resetMembers = () => {
    setPromotedLobIds(new Set());
    setPromotedSubOrgIds(new Set());
    setMembersConfirmed(false);
  };

  // Advance past the interim "New members" step. Promote-all (nothing opted out) needs no server round-trip —
  // the Pass-1 token already unioned every new member. If the user opted any member out, re-project the
  // PINNED exports via the refine route (no re-export / re-fetch) and carry its fresh token forward.
  const handleConfirmMembers = async () => {
    if (!preview) {
      return;
    }
    const allLobIds = (preview.new_members?.lobs ?? []).map((member) => member.external_id);
    const allSubOrgIds = (preview.new_members?.sub_organizations ?? []).map((member) => member.external_id);
    const optedOutAny =
      allLobIds.some((id) => !promotedLobIds.has(id)) || allSubOrgIds.some((id) => !promotedSubOrgIds.has(id));
    if (!optedOutAny) {
      setMembersConfirmed(true);
      return;
    }
    try {
      setIsRefining(true);
      const body: RefinePreviewParams = {
        preview_token: preview.preview_token,
        included_new_member_ids: {
          lobs: allLobIds.filter((id) => promotedLobIds.has(id)),
          sub_organizations: allSubOrgIds.filter((id) => promotedSubOrgIds.has(id)),
        },
      };
      const { data } = await axios.post<PromotionPreviewResult>(`${baseRoute}/preview/refine`, body);
      setPreview(data);
      setMembersConfirmed(true);
    } catch (error) {
      // The refine failed (a stale opt-in id, the handoff expired, or a change names an item the target doesn't
      // have, which the error dialog lists by its reasons) — reset so the user re-previews from a clean state
      // rather than carrying a token that no longer matches the reviewed change list.
      await reportAxiosError(error);
      setPreview(null);
      resetSelection();
      resetMembers();
    } finally {
      setIsRefining(false);
    }
  };

  // Phase gating: after preview, if the diff introduces new members show the interim opt-in step first;
  // otherwise (or once confirmed) show the change selector. `hasNewMembers` is false for the common
  // promotion, so this collapses to the prior preview → selector flow with no extra step.
  const showMemberStep = Boolean(preview) && hasNewMembers(preview?.new_members) && !membersConfirmed;
  const showChangeSelector = Boolean(preview) && !showMemberStep;

  return {
    promotedLobIds,
    promotedSubOrgIds,
    isRefining,
    showMemberStep,
    showChangeSelector,
    seedPromotedMembers,
    handleToggleMember,
    handleConfirmMembers,
    resetMembers,
  };
}
